import { iatan2, imax, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, SendaNpc } from '../types.ts';
import type { AllyRules, HornRules } from '../roca-types.ts';
import { damageEnemy, ev } from './combat.ts';

/**
 * What the Long Hunt's chapter 5 shares (docs/sendas/barbaro.md, «La guarida»): Ulla and Ivar fight beside the hero
 * («como un héroe chico»: a blow every so often at what is nearest, where they stand; the path's base moves them), and
 * Ulla's horn freezes the pack round her. Cost: one look at the nightmares a tick for each of the two.
 */

export const rocaNpc = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id);

/** One blow from an ally if its time has come: at what is nearest within reach (`skip` left out). Returns its next tick. */
export const allyStrike = (s: BattleState, n: SendaNpc | undefined, a: AllyRules, cd: number, skip?: (e: Enemy) => boolean): number => {
  if (!n || n.hp <= 0 || n.away || n.flinch > 0 || s.tick < cd) return cd;
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of s.enemies) {
    if (e.hp <= 0 || (skip && skip(e))) continue;
    if (!withinRange(e.x, e.y, n.x, n.y, a.reach)) continue;
    const d = Math.abs(e.x - n.x) + Math.abs(e.y - n.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  if (!best) return cd;
  if (best.x !== n.x || best.y !== n.y) n.facing = iatan2(best.y - n.y, best.x - n.x);
  damageEnemy(s, best, a.hit, { pid: '' });
  ev(s, { t: 'senda', kind: 'work', npc: n.id, x: best.x, y: best.y });
  return s.tick + a.every;
};

/**
 * Ulla's horn: every one of the pack within reach of her frozen `scare` (never `boss`, whose howl the caller cuts).
 * Returns how many it froze.
 */
export const blowHorn = (s: BattleState, n: SendaNpc | undefined, h: HornRules, boss = 0): number => {
  if (!n || n.hp <= 0) return 0;
  let k = 0;
  for (const e of s.enemies) {
    if (e.hp <= 0 || e.id === boss || !withinRange(e.x, e.y, n.x, n.y, h.reach)) continue;
    e.stun = imax(e.stun, h.scare);
    k++;
  }
  ev(s, { t: 'senda', kind: 'rHorn', npc: n.id, x: n.x, y: n.y, n: k });
  return k;
};
